import { useEffect } from 'react'

import { useAssociateDevice } from '../hooks/useAssociateDevice'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Button,
  CloseIcon,
  ColorScheme,
  Flex,
  IconButton,
  Stack,
  Text,
} from '@hivewatch/dark'

import { ControlledSelect } from '@/components/ui'
import { DeviceType } from '@/graphql/generated/schemas'
import { tVar } from '@/utils/i18n'

import { EditTailgateGridButton } from '../components/EditTailgateGridButton'
import { EditDeviceFieldsI } from '../types/types'
import { DarkEditDeviceSection } from './DarkEditDeviceSection'
import { DarkEditDeviceSkeleton } from './DarkEditDeviceSkeleton'

interface DarkAssociateDevicesIProps {
  colorScheme: ColorScheme
  deviceData: EditDeviceFieldsI
  isOtherSectionLoading: boolean
  onSave?: () => void
  /** Overlay z-index for the nested tailgate/associate modals. Required when
   * the edit modal itself is elevated (drawer, stacked modals), so the nested
   * modals don't open underneath it. */
  overlayZIndex?: number
}

interface AssociatedDevicesFormI {
  associateDevice: {
    label: string
    value: string
  }
}

export const DarkAssociateDevices = ({
  colorScheme,
  deviceData,
  isOtherSectionLoading,
  onSave,
  overlayZIndex,
}: DarkAssociateDevicesIProps) => {
  const { t } = useTranslation()
  const deviceType = deviceData?.type
  const { control, watch, reset } = useForm<AssociatedDevicesFormI>({
    mode: 'onChange',
  })
  const {
    associatedDeviceOptions,
    data,
    selectedDevices,
    addSelectedDeviceOption,
    handleAssociation,
    removeSelectedDeviceOption,
    isDirty,
    isLoading,
  } = useAssociateDevice({ deviceData })
  const selectedOption = watch('associateDevice')
  const isDoor = deviceType === DeviceType.Door
  const isCamera = deviceType === DeviceType.Camera
  const tailgateConnectionId = isDoor
    ? data?.device?.tailgateConns?.edges?.[0]?.node?.id
    : undefined
  const doorConnectionMap: Record<string, string> = isCamera
    ? (data?.device?.tailgateConns?.edges ?? []).reduce((map, edge) => {
        const connection = edge?.node
        if (connection?.door?.id) map[connection.door.id] = connection.id
        return map
      }, {} as Record<string, string>)
    : {}

  useEffect(() => {
    if (!selectedOption) return

    addSelectedDeviceOption(selectedOption)
    reset({ associateDevice: null })
  }, [addSelectedDeviceOption, reset, selectedOption])

  if (isOtherSectionLoading) return <DarkEditDeviceSkeleton />

  const rowBg = colorScheme === 'light' ? 'light.bg.card' : 'bg.card'
  const rowBorder =
    colorScheme === 'light' ? 'light.border.subtle' : 'border.subtle'

  return (
    <DarkEditDeviceSection
      actions={
        <Button
          colorScheme={colorScheme}
          disabled={!isDirty}
          isLoading={isLoading}
          onClick={async () => {
            await handleAssociation()
            onSave?.()
          }}
        >
          {isLoading ? t('common:saving') : t('common:save')}
        </Button>
      }
      colorScheme={colorScheme}
      description={tVar(t, 'devices:crud_associated_devices_description', {
        device: deviceType?.toLowerCase(),
      })}
      title={t('devices:crud_associated_devices_title')}
    >
      <Box maxW='600px'>
        <ControlledSelect
          colorScheme={colorScheme}
          control={control}
          name='associateDevice'
          options={associatedDeviceOptions}
          placeholder={t('devices:crud_associate_devices_placeholder')}
        />
      </Box>

      <Stack gap='8px' maxH='550px' mt='16px' overflowY='auto'>
        {selectedDevices.map((selectedDevice) => (
          <Flex
            align='center'
            bg={rowBg}
            border='1px solid'
            borderColor={rowBorder}
            gap='8px'
            justify='space-between'
            key={selectedDevice.value}
            minH='48px'
            px='12px'
            py='8px'
          >
            <Text flex='1' fontSize='14px'>
              {selectedDevice.label}
            </Text>

            {isDoor && tailgateConnectionId && (
              <EditTailgateGridButton
                cameraId={selectedDevice.value}
                cameraName={selectedDevice.label}
                // The discriminator for the dark associate/tailgate modals;
                // without it the button opens the legacy Chakra v1 modals
                colorScheme={colorScheme}
                connectionId={tailgateConnectionId}
                deviceType={selectedDevice.type}
                doorId={deviceData.id}
                doorName={deviceData.name}
                onSaveCallback={onSave}
                overlayZIndex={overlayZIndex}
                renderTrigger={(onClick, isDisabled) => (
                  <Button
                    colorScheme={colorScheme}
                    disabled={isDisabled}
                    onClick={onClick}
                    size='sm'
                    variant='secondary'
                  >
                    {t('devices:crud_edit_tailgating_aria')}
                  </Button>
                )}
              />
            )}
            {isCamera && doorConnectionMap[selectedDevice.value] && (
              <EditTailgateGridButton
                cameraId={deviceData.id}
                cameraName={deviceData.name}
                // The discriminator for the dark associate/tailgate modals;
                // without it the button opens the legacy Chakra v1 modals
                colorScheme={colorScheme}
                connectionId={doorConnectionMap[selectedDevice.value]}
                deviceType={selectedDevice.type}
                doorId={selectedDevice.value}
                doorName={selectedDevice.label}
                onSaveCallback={onSave}
                overlayZIndex={overlayZIndex}
                renderTrigger={(onClick, isDisabled) => (
                  <Button
                    colorScheme={colorScheme}
                    disabled={isDisabled}
                    onClick={onClick}
                    size='sm'
                    variant='secondary'
                  >
                    {t('devices:crud_edit_tailgating_aria')}
                  </Button>
                )}
              />
            )}

            <IconButton
              aria-label={t('common:remove')}
              colorScheme={colorScheme}
              icon={<CloseIcon />}
              onClick={() => removeSelectedDeviceOption(selectedDevice)}
            />
          </Flex>
        ))}
      </Stack>
    </DarkEditDeviceSection>
  )
}
